import { useRef, useState } from "react";
import { CanvasElementView } from "./components/CanvasElement";
import { PAGE_DIMENSIONS_MM, PX_PER_MM, type CanvasElement, type PageSize } from "./types";

interface Props {
  elements: CanvasElement[];
  pageSize: PageSize;
  /** Real report data keyed by field_key, fetched from the owning domain's own endpoint (never apps.printing's). */
  data: Record<string, unknown>;
  fileName?: string;
}

export function PrintPreview({ elements, pageSize, data, fileName = "print-preview.pdf" }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { widthMm, heightMm } = PAGE_DIMENSIONS_MM[pageSize];
  const [exportWarnings, setExportWarnings] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  async function handleExportPdf() {
    setExporting(true);
    const warnings: string[] = [];
    try {
      const { default: jsPDF } = await import("jspdf");
      const { default: html2canvas } = await import("html2canvas");

      const pages = containerRef.current?.querySelectorAll<HTMLElement>(".print-page");
      if (!pages || pages.length === 0) {
        throw new Error("صفحه‌ای برای تولید PDF یافت نشد.");
      }

      const doc = new jsPDF({
        orientation: heightMm >= widthMm ? "portrait" : "landscape",
        unit: "mm",
        format: [widthMm, heightMm],
      });

      for (let i = 0; i < pages.length; i++) {
        if (i > 0) {
          doc.addPage([widthMm, heightMm], heightMm >= widthMm ? "portrait" : "landscape");
        }
        const pageEl = pages[i];
        if (!pageEl) continue;
        const canvas = await html2canvas(pageEl, {
          scale: 2.5, // High density crisp output
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });

        const imgData = canvas.toDataURL("image/png");
        doc.addImage(imgData, "PNG", 0, 0, widthMm, heightMm);
      }

      doc.save(fileName);
    } catch (err) {
      console.error("Failed to export PDF:", err);
      warnings.push(err instanceof Error ? err.message : "خطا در تولید فایل PDF.");
    } finally {
      setExportWarnings(warnings);
      setExporting(false);
    }
  }

  // Pagination logic: find repeating tables or groups to split across pages
  let itemsPerPage = 14;
  let totalItems = 0;
  const repeatingEl = elements.find((el) => el.type === "table" || el.type === "group");

  if (repeatingEl) {
    const fieldKey = repeatingEl.type === "table" ? repeatingEl.field_key : repeatingEl.repeat_source || "items";
    const rawItems = data[fieldKey];
    if (Array.isArray(rawItems)) {
      totalItems = rawItems.length;
    }
    if (repeatingEl.type === "group" && repeatingEl.row_height) {
      itemsPerPage = Math.max(1, Math.floor((repeatingEl.h || 15) / repeatingEl.row_height));
    } else if (repeatingEl.type === "table") {
      const containerPx = (repeatingEl.h || 15) * 40;
      const headerPx = 32;
      const safeRowPx = 28;
      const maxRows = Math.floor((containerPx - headerPx) / safeRowPx);
      itemsPerPage = Math.max(1, maxRows);
    }
  }

  const pageCount = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const pageIndexes = Array.from({ length: pageCount }, (_, i) => i);

  return (
    <div style={{ direction: "rtl" }}>
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            data-testid="export-pdf-btn"
            onClick={handleExportPdf}
            disabled={exporting}
            style={{ padding: "6px 16px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
          >
            {exporting ? "در حال تولید PDF..." : "خروجی PDF"}
          </button>
          <button
            type="button"
            data-testid="browser-print-btn"
            onClick={() => window.print()}
            style={{ padding: "6px 16px", background: "#059669", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
          >
            🖨️ چاپ مرورگر ({pageCount} صفحه)
          </button>
        </div>
        {pageCount > 1 && (
          <span style={{ fontSize: 13, color: "#4b5563", fontWeight: 600 }}>
            تعداد کل صفحات: {pageCount} برگه A4 (بدون اسکرول)
          </span>
        )}
      </div>

      {exportWarnings.length > 0 && (
        <div role="alert" data-testid="export-warnings" style={{ color: "#b45309", marginBottom: 8 }}>
          PDF تولید شد، اما {exportWarnings.length} تصویر بارگذاری نشد:
          <ul>
            {exportWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div ref={containerRef} className="print-preview-container" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {pageIndexes.map((pageIdx) => (
          <div
            key={pageIdx}
            data-testid="print-preview-surface"
            className="print-preview-surface print-page"
            style={{
              position: "relative",
              width: widthMm * PX_PER_MM,
              height: heightMm * PX_PER_MM,
              background: "#fff",
              border: "1px solid #d1d5db",
              margin: "0 auto",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              pageBreakAfter: pageIdx < pageCount - 1 ? "always" : "auto",
              breakAfter: pageIdx < pageCount - 1 ? "page" : "auto",
            }}
          >
            {/* Page header tag */}
            {pageCount > 1 && (
              <div
                className="no-print"
                style={{
                  position: "absolute",
                  top: 4,
                  left: 4,
                  fontSize: 10,
                  color: "#9ca3af",
                  background: "#f3f4f6",
                  padding: "2px 6px",
                  borderRadius: 4,
                  zIndex: 10,
                }}
              >
                صفحه {pageIdx + 1} از {pageCount}
              </div>
            )}

            {elements.map((el) => (
              <CanvasElementView
                key={el.id}
                element={el}
                data={data}
                rowOffset={pageIdx * itemsPerPage}
                rowLimit={itemsPerPage}
              />
            ))}
          </div>
        ))}
      </div>

      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 0;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
            width: 210mm !important;
            height: auto !important;
            overflow: visible !important;
          }
          body * { visibility: hidden !important; }
          .print-preview-container, .print-preview-container * { visibility: visible !important; }
          .print-preview-container {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 210mm !important;
            margin: 0 !important;
            padding: 0 !important;
            gap: 0 !important;
          }
          .print-page {
            position: relative !important;
            width: 210mm !important;
            height: 297mm !important;
            page-break-after: always !important;
            break-after: page !important;
            page-break-inside: avoid !important;
            box-shadow: none !important;
            border: none !important;
            margin: 0 auto !important;
            outline: none !important;
          }
          .print-page:last-child {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
          .print-page * {
            outline: none !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}

